import { CalendarDate } from '@internationalized/date';
import React__default from 'react';
import { useController } from 'react-hook-form';
import { DateInput } from '../../../atoms/DateInput.js';
import { FormItem } from '../FormItem.js';
import { ReadOnlyField, formatReadOnlyValue } from './ReadOnlyFormField.js';

/**
 * DateFormField - A form field component for date input.
 *
 * @example
 * ```tsx
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   <DateFormField
 *     schemaField="birthDate"
 *     labelText="Birth Date"
 *   />
 * </Form>
 * ```
 */
function DateFormField({ schemaField, labelText, descriptionText, isReadOnly, isDisabled, required, className, id, orientation, linkedField, showFieldToggle, ...dateInputProps }) {
    const { field } = useController({
        name: schemaField,
        shouldUnregister: false,
    });
    // Convert JavaScript Date to CalendarDate for DateInput
    const dateValue = React__default.useMemo(() => {
        if (!field.value)
            return null;
        if (field.value instanceof Date) {
            // Convert Date to CalendarDate using UTC date components to preserve the intended date
            // This ensures that a date like '2024-06-15' is preserved as June 15th, not June 14th
            // when in timezones behind UTC (like EDT GMT-0400)
            const year = field.value.getUTCFullYear();
            const month = field.value.getUTCMonth() + 1; // getUTCMonth is 0-indexed, CalendarDate is 1-indexed
            const day = field.value.getUTCDate();
            // Use CalendarDate constructor directly to avoid string parsing and timezone issues
            return new CalendarDate(year, month, day);
        }
        // If it's already a DateValue, return as is
        if (field.value && typeof field.value === 'object' && 'year' in field.value) {
            return field.value;
        }
        return null;
    }, [field.value]);
    // Handle onChange: convert CalendarDate back to JavaScript Date
    const handleChange = React__default.useCallback((value) => {
        if (value) {
            // Convert CalendarDate to JavaScript Date at midnight UTC
            // This ensures consistent date representation regardless of timezone
            // Note: month is 1-indexed in CalendarDate, but 0-indexed in Date constructor
            const date = new Date(Date.UTC(value.year, value.month - 1, value.day));
            field.onChange(date);
        }
        else {
            field.onChange(null);
        }
    }, [field]);
    if (isReadOnly) {
        return (React__default.createElement(ReadOnlyField, { label: labelText, value: formatReadOnlyValue(field.value, 'date'), description: descriptionText, required: required, className: className }));
    }
    return (React__default.createElement(FormItem, { schemaField: schemaField, labelText: labelText, descriptionText: descriptionText, required: required, className: className, id: id, isDisabled: isDisabled, orientation: orientation, linkedField: linkedField, showFieldToggle: showFieldToggle },
        React__default.createElement(DateInput, { ...dateInputProps, value: dateValue, onChange: handleChange, onBlur: field.onBlur, isDisabled: isDisabled, isReadOnly: isReadOnly })));
}

export { DateFormField };
//# sourceMappingURL=DateFormField.js.map
